import React, { forwardRef, useCallback, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Tooltip from "components/tooltips/Tooltips";
import ExternalTooltip from "components/tooltips/ExternalTooltip";
import UseContext from "hooks/useContext";

const GRAY_700 = "#374151";

const SidebarUnitDesktop = forwardRef(function SidebarUnitDesktop(
    {
        routeSidebar,
        routeName,
        icon: IconComponent,
        text,
        exactMatch = false,
        children,
        isSubItem = false,
        onboardingTitle,
        onboardingDescription,
        onboardingStorageKey,
        onboardingPointerPlacement,
    },
    ref
) {
    const { sidebarExpanded } = UseContext();
    const location = useLocation();
    const pathname = location.pathname;

    const hasOnboarding =
        onboardingTitle &&
        onboardingDescription &&
        onboardingStorageKey;

    const [onboardingConfirm, setOnboardingConfirm] = useState(() => {
        if (!onboardingStorageKey) return true;
        try {
            return !!localStorage.getItem(onboardingStorageKey);
        } catch {
            return true;
        }
    });

    const handleOnboardingAck = useCallback(
        (v) => {
            if (!v || !onboardingStorageKey) return;
            try {
                localStorage.setItem(onboardingStorageKey, "1");
            } catch {
                /* indisponible */
            }
            setOnboardingConfirm(true);
        },
        [onboardingStorageKey]
    );

    const isActive = exactMatch
        ? pathname === routeSidebar
        : routeSidebar === "/contract/toBeSigned"
          ? pathname.includes("/contract")
          : routeSidebar === "/invoice/toBeSet"
            ? pathname.includes("/invoice")
            : routeSidebar === "/timeSheet/toBeChecked"
              ? pathname.includes("/timeSheet")
              : routeSidebar === "/profil/myAccountCompany"
                ? pathname.includes("/profil")
                : routeSidebar === "/missions" ||
                    routeSidebar === "/missions/end" ||
                    routeSidebar === "/missions/inProgress"
                  ? pathname.includes("/missions") ||
                    pathname.includes("/mission/")
                  : pathname.includes(routeSidebar) ||
                    pathname === routeSidebar;

    const linkBlock = (
        <Tooltip
            text={routeName}
            disabled={sidebarExpanded}
            placement="right"
        >
            <NavLink
                end={exactMatch}
                to={routeSidebar}
                className={({ isActive: navIsActive }) => {
                    const active = exactMatch
                        ? navIsActive
                        : navIsActive || isActive;
                    const baseClasses = `flex items-center justify-center h-[44px] rounded-lg py-3 transition duration-150 ${!sidebarExpanded ? "px-0" : "px-2"}`;
                    if (isSubItem && active) {
                        return `${baseClasses} hover:bg-gray-100 text-gray-700 bg-gray-100`;
                    }
                    return `${baseClasses} ${
                        active
                            ? "text-gray-700 bg-gray-100"
                            : "text-gray-700 bg-transparent hover:bg-gray-100"
                    }`;
                }}
                style={({ isActive: navIsActive }) => {
                    const active = exactMatch
                        ? navIsActive
                        : navIsActive || isActive;
                    if (isSubItem && active) {
                        return { backgroundColor: "#F3F4F6" };
                    }
                    return {};
                }}
            >
                {sidebarExpanded ? (
                    <div id={routeName} className="flex items-center w-full">
                        {IconComponent && (
                            <span className="shrink-0">
                                <IconComponent wh={20} color={GRAY_700} />
                            </span>
                        )}
                        {text && (
                            <span
                                className={`ft-sm font-normal ${IconComponent ? "ml-4" : ""}`}
                            >
                                {text}
                            </span>
                        )}
                        {!IconComponent && !text && children}
                        {children && (IconComponent || text) && (
                            <div className="ml-auto">{children}</div>
                        )}
                    </div>
                ) : (
                    <div
                        id={routeName}
                        className="flex items-center justify-center"
                    >
                        {IconComponent && (
                            <IconComponent wh={24} color={GRAY_700} />
                        )}
                        {!IconComponent && children}
                    </div>
                )}
            </NavLink>
        </Tooltip>
    );

    return (
        <li ref={ref}>
            {hasOnboarding ? (
                <ExternalTooltip
                    title={onboardingTitle}
                    description={onboardingDescription}
                    confirm={onboardingConfirm}
                    setOnClickConfirm={handleOnboardingAck}
                    pointerPlacement={onboardingPointerPlacement ?? "bottom"}
                    triggerClassName="relative block w-full"
                >
                    {linkBlock}
                </ExternalTooltip>
            ) : (
                linkBlock
            )}
        </li>
    );
});

SidebarUnitDesktop.displayName = "SidebarUnitDesktop";

export default SidebarUnitDesktop;
